import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import fleetApi from '../../../services/api.service';

const getErrorMessage = (error) =>
  error?.response?.data?.message || error?.message;

/**
 * Access of a tenant to the public API documentation ("Documentation" block of the "Clés API"
 * tab).
 *
 * Loads the documentation access state when the tab opens. Enabling and regenerating respond with
 * the new state and, when it has just been generated, the plaintext password: this is the only
 * time it exists on the screen side (one-time secret), and the hook does not keep it (it hands it
 * back to the caller, which displays it).
 * Errors are shown as a notification and the actions then return null, like the other hooks of
 * the tool.
 *
 * @param {number} clientId - The tenant identifier
 */
export default function useClientApiDocsAccess(clientId) {
  const [access, setAccess] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // True when the last load failed: the screen must say so instead of loading forever.
  const [loadError, setLoadError] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  // A stale response that arrives after unmount (tab change) is ignored.
  useEffect(() => {
    let isActive = true;

    const load = async () => {
      try {
        setIsLoading(true);
        setLoadError(false);

        const response = await fleetApi.get(
          `/super-admin/clients/${clientId}/api-docs-access`
        );

        if (isActive) setAccess(response.data.access);
      } catch (error) {
        if (isActive) {
          setLoadError(true);
          toast.error(getErrorMessage(error));
        }
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    load();

    return () => {
      isActive = false;
    };
  }, [clientId, reloadCount]);

  const reload = () => setReloadCount((count) => count + 1);

  /**
   * Enables or disables the access. On first activation, the response carries the generated
   * password.
   * No notification when a password is returned: the window that displays it takes care of it
   * (only one notification at a time, which could cover it).
   * @param {boolean} isEnabled
   * @returns {Promise<{ password: string|null }|null>} The password (or null if there is no new
   *   one), or null if the call failed
   */
  const setEnabled = async (isEnabled) => {
    try {
      setIsSaving(true);

      const response = await fleetApi.put(
        `/super-admin/clients/${clientId}/api-docs-access`,
        { is_enabled: isEnabled }
      );

      setAccess(response.data.access);
      if (!response.data.password) toast.success(response.data.message);

      return { password: response.data.password, access: response.data.access };
    } catch (error) {
      toast.error(getErrorMessage(error));
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Generates a new password: the old one and the open sessions are rejected immediately.
   * @returns {Promise<{ password: string }|null>}
   */
  const regeneratePassword = async () => {
    try {
      setIsSaving(true);

      const response = await fleetApi.post(
        `/super-admin/clients/${clientId}/api-docs-access/regenerate-password`
      );

      setAccess(response.data.access);

      return { password: response.data.password, access: response.data.access };
    } catch (error) {
      toast.error(getErrorMessage(error));
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    access,
    isLoading,
    loadError,
    reload,
    isSaving,
    setEnabled,
    regeneratePassword,
  };
}
